/**
 * Utilitar pentru capturarea hartii OpenLayers la o dimensiune fizica data (cm + dpi), cu text si
 * simboluri lizibile la print.
 *
 * Pentru ca textul/simbolurile sa fie mai mari si clare (nu micsorate ca la supersampling), randam
 * acelasi extent printr-o harta temporara off-screen cu pixelRatio = dpi / 96. La acel pixelRatio
 * OpenLayers deseneaza fonturi/linii/simboluri mai mari.
 *
 * IMPORTANT: nu reutilizam instantele de layer ale hartii live (ar lasa harta live alba / capturi
 * goale). In schimb cream layere noi peste ACELEASI surse (sursele pot fi partajate intre harti).
 */

import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import VectorImageLayer from 'ol/layer/VectorImage';
import LayerGroup from 'ol/layer/Group';
import WebGLLayer from '../layer/WebGLLayer';

const CM_PER_INCH = 2.54;
const CSS_DPI = 96; // referinta CSS: 96px = 1 inch
const METERS_PER_INCH = 0.0254;

// --- tabel CRC32 pentru recalcularea CRC-ului chunk-urilor PNG ---
const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) {
            c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
        }
        table[n] = c >>> 0;
    }
    return table;
})();

function crc32(bytes, start, end) {
    let c = 0xFFFFFFFF;
    for (let i = start; i < end; i++) {
        c = CRC_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
    }
    return (c ^ 0xFFFFFFFF) >>> 0;
}

/**
 * Rescrie/insereaza chunk-ul pHYs intr-un PNG ca sa declare DPI-ul corect.
 * Canvas.toBlob scrie mereu 96 dpi; aici punem dpi-ul cerut (pixels-per-metre).
 *
 * @param {ArrayBuffer} pngBuffer
 * @param {number} dpi
 * @returns {Blob}
 */
function setPngDpi(pngBuffer, dpi) {
    const src = new Uint8Array(pngBuffer);
    const ppm = Math.round(dpi / METERS_PER_INCH); // pixels per metre

    // construieste chunk-ul pHYs: len(4) + "pHYs"(4) + data(9) + crc(4)
    const phys = new Uint8Array(4 + 4 + 9 + 4);
    const dv = new DataView(phys.buffer);
    dv.setUint32(0, 9);                 // lungime data = 9
    phys[4] = 0x70; phys[5] = 0x48; phys[6] = 0x59; phys[7] = 0x73; // "pHYs"
    dv.setUint32(8, ppm);               // x pixels per unit
    dv.setUint32(12, ppm);              // y pixels per unit
    phys[16] = 1;                       // unit = metre
    dv.setUint32(17, crc32(phys, 4, 17)); // CRC peste type + data

    // semnatura PNG (8) urmata de IHDR (len 4 + type 4 + data 13 + crc 4 = 25) => IHDR se termina la 33
    const ihdrEnd = 8 + 4 + 4 + 13 + 4;

    // daca exista deja un pHYs imediat dupa IHDR, il inlocuim; altfel il inseram
    let insertAt = ihdrEnd;
    let removeLen = 0;
    if (src[ihdrEnd + 4] === 0x70 && src[ihdrEnd + 5] === 0x48 &&
        src[ihdrEnd + 6] === 0x59 && src[ihdrEnd + 7] === 0x73) {
        const existingLen = new DataView(src.buffer, src.byteOffset).getUint32(ihdrEnd);
        removeLen = 4 + 4 + existingLen + 4;
    }

    const out = new Uint8Array(src.length - removeLen + phys.length);
    out.set(src.subarray(0, insertAt), 0);
    out.set(phys, insertAt);
    out.set(src.subarray(insertAt + removeLen), insertAt + phys.length);

    return new Blob([out], { type: 'image/png' });
}

/**
 * Cloneaza un layer reutilizand aceeasi sursa si stil, fara a atinge layer-ul original.
 * Returneaza null pentru layere ne-vizibile sau nesuportate.
 */
function cloneLayer(layer) {
    if (!layer || layer.getVisible() === false) return null;

    const common = {
        opacity: layer.getOpacity(),
        zIndex: layer.getZIndex(),
        minResolution: layer.getMinResolution(),
        maxResolution: layer.getMaxResolution(),
        extent: layer.getExtent()
    };

    if (layer instanceof LayerGroup) {
        const sub = [];
        layer.getLayers().getArray().forEach(child => {
            const c = cloneLayer(child);
            if (c) sub.push(c);
        });
        return new LayerGroup(Object.assign({}, common, { layers: sub }));
    }

    if (typeof layer.getSource !== 'function') return null;
    const source = layer.getSource();
    if (!source) return null;

    // layer WebGL custom: stilul e pe customStyle, aplicat in createRenderer()
    if (layer instanceof WebGLLayer) {
        const clone = new WebGLLayer(Object.assign({}, common, { source: source }));
        if (layer.customStyle != null) clone.setCustomStyle(layer.customStyle);
        return clone;
    }

    const opts = Object.assign({}, common, { source: source });

    if (layer instanceof VectorImageLayer) {
        if (layer.getStyle()) opts.style = layer.getStyle();
        return new VectorImageLayer(opts);
    }
    if (layer instanceof VectorLayer) {
        if (layer.getStyle()) opts.style = layer.getStyle();
        return new VectorLayer(opts);
    }
    if (layer instanceof TileLayer) {
        return new TileLayer(opts);
    }
    if (layer instanceof ImageLayer) {
        return new ImageLayer(opts);
    }

    return null;
}

/**
 * Captureaza harta la o dimensiune fizica (cm) si o rezolutie (dpi) si returneaza un PNG cu
 * numarul de pixeli corespunzator (cm -> inch -> * dpi). Textul/simbolurile sunt scalate la dpi.
 *
 * @param {import('ol/Map').default} map - instanta OpenLayers Map (harta live)
 * @param {object} options
 * @param {number} options.widthCm  - latimea imaginii in cm
 * @param {number} options.heightCm - inaltimea imaginii in cm
 * @param {number} [options.dpi=150] - rezolutia in dots-per-inch
 * @returns {Promise<{ canvas: HTMLCanvasElement, dataUrl: string, blob: Blob, width: number, height: number }>}
 */
export function captureMapViewPng(map, options) {
    return new Promise((resolve, reject) => {
        let printMap = null;
        let printDiv = null;

        const cleanup = () => {
            try { if (printMap) printMap.setTarget(undefined); } catch (e) { /* ignore */ }
            try { if (printDiv && printDiv.parentNode) printDiv.parentNode.removeChild(printDiv); } catch (e) { /* ignore */ }
        };

        try {
            const opts = options || {};
            const widthCm = opts.widthCm;
            const heightCm = opts.heightCm;
            const dpi = opts.dpi != null ? opts.dpi : 150;

            if (!(widthCm > 0) || !(heightCm > 0)) {
                reject(new Error('captureMapViewPng: widthCm si heightCm trebuie sa fie > 0.'));
                return;
            }

            // cm -> inch -> pixeli de print (dimensiunea finala a PNG-ului, in CSS px)
            const printCssPx = [
                Math.round(widthCm / CM_PER_INCH * dpi),
                Math.round(heightCm / CM_PER_INCH * dpi)
            ];

            // pixelRatio = "deseneaza ca pentru un ecran de <dpi> DPI" => fonturi/linii/simboluri mai mari
            const pixelRatio = dpi / CSS_DPI;

            const view = map.getView();
            const screenExtent = view.calculateExtent(map.getSize());

            // cloneaza layerele live peste aceleasi surse
            const clonedLayers = [];
            map.getLayers().getArray().forEach(layer => {
                const c = cloneLayer(layer);
                if (c) clonedLayers.push(c);
            });

            // container detasat, in afara ecranului, dimensionat la cutia de print (CSS px)
            printDiv = document.createElement('div');
            printDiv.style.position = 'absolute';
            printDiv.style.left = '-99999px';
            printDiv.style.top = '0';
            printDiv.style.width = `${printCssPx[0]}px`;
            printDiv.style.height = `${printCssPx[1]}px`;
            printDiv.style.overflow = 'hidden';
            document.body.appendChild(printDiv);

            const printView = new View({
                projection: view.getProjection(),
                center: view.getCenter(),
                rotation: view.getRotation(),
                resolution: view.getResolution(),
                constrainResolution: false
            });

            printMap = new Map({
                target: printDiv,
                layers: clonedLayers,
                view: printView,
                pixelRatio: pixelRatio,
                controls: [],
                interactions: []
            });

            // forteaza OL sa preia dimensiunea div-ului dupa ce e in DOM
            printMap.setSize(printCssPx);
            printMap.updateSize();

            // Fit pe extent-ul ecranului in cutia de print: pastreaza aceeasi zona vizibila si
            // umple toata imaginea (zoom usor diferit pe axa care nu se potriveste ca raport).
            // Asa nu iesim in afara extent-ului layerelor (evita benzile transparente sus/jos).
            printView.fit(screenExtent, { size: printCssPx, constrainResolution: false });

            printMap.once('rendercomplete', () => {
                try {
                    // Compoziteaza in dimensiunea REALA randata (CSS px * pixelRatio efectiv folosit de OL).
                    // O luam din primul canvas de layer ca sa nu ghicim: layer-ul ocupa tot viewport-ul.
                    const layerCanvases = Array.from(printDiv.querySelectorAll('canvas.ol-layer, .ol-layer canvas'))
                        .filter(c => c.width > 0 && c.height > 0);

                    let renderW = Math.round(printCssPx[0] * pixelRatio);
                    let renderH = Math.round(printCssPx[1] * pixelRatio);
                    if (layerCanvases.length > 0) {
                        renderW = layerCanvases[0].width;
                        renderH = layerCanvases[0].height;
                    }

                    const mapCanvas = document.createElement('canvas');
                    mapCanvas.width = renderW;
                    mapCanvas.height = renderH;
                    const ctx = mapCanvas.getContext('2d');
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';

                    // Compozitam in dimensiunea reala de device-pixeli (renderW/H), egala cu pixelii
                    // canvas-ului de layer. transform-ul CSS (ex. matrix(0.32,...)) e DOAR pentru
                    // afisarea in DOM (micsoreaza canvas-ul mare la cutia CSS) - NU il aplicam la
                    // compozitare, altfel desenam harta intr-un colt. Desenam 1:1.
                    layerCanvases.forEach(layerCanvas => {
                        ctx.globalAlpha = Number(layerCanvas.parentNode.style.opacity || layerCanvas.style.opacity || 1);
                        ctx.setTransform(1, 0, 0, 1, 0, 0);
                        const bg = layerCanvas.parentNode.style.backgroundColor;
                        if (bg) {
                            ctx.fillStyle = bg;
                            ctx.fillRect(0, 0, mapCanvas.width, mapCanvas.height);
                        }
                        ctx.drawImage(layerCanvas, 0, 0);
                    });

                    ctx.globalAlpha = 1;

                    // Downscale la exact cm*dpi (printCssPx) pentru output determinist; randarea la
                    // pixelRatio mare pastreaza textul/simbolurile clare.
                    let outCanvas = mapCanvas;
                    if (mapCanvas.width !== printCssPx[0] || mapCanvas.height !== printCssPx[1]) {
                        outCanvas = document.createElement('canvas');
                        outCanvas.width = printCssPx[0];
                        outCanvas.height = printCssPx[1];
                        const outCtx = outCanvas.getContext('2d');
                        outCtx.imageSmoothingEnabled = true;
                        outCtx.imageSmoothingQuality = 'high';
                        outCtx.drawImage(mapCanvas, 0, 0, printCssPx[0], printCssPx[1]);
                    }

                    outCanvas.toBlob(blob => {
                        // canvas.toBlob scrie mereu 96 dpi; rescriem pHYs cu dpi-ul cerut
                        blob.arrayBuffer().then(buf => {
                            const dpiBlob = setPngDpi(buf, dpi);
                            cleanup();
                            resolve({
                                canvas: outCanvas,
                                dataUrl: outCanvas.toDataURL('image/png'),
                                blob: dpiBlob,
                                width: outCanvas.width,
                                height: outCanvas.height
                            });
                        }).catch(err => {
                            // daca rescrierea esueaza, returnam blob-ul original (pixelii sunt corecti)
                            cleanup();
                            resolve({
                                canvas: outCanvas,
                                dataUrl: outCanvas.toDataURL('image/png'),
                                blob: blob,
                                width: outCanvas.width,
                                height: outCanvas.height
                            });
                        });
                    }, 'image/png');
                } catch (err) {
                    cleanup();
                    reject(err);
                }
            });

            // render normal (nu renderSync) ca sursele async (tile/image) sa apuce sa incarce
            printMap.render();
        } catch (err) {
            cleanup();
            reject(err);
        }
    });
}
